export const FIRST_TOUCH_STORAGE_KEY = "bm_first_touch";

const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "ref",
] as const;

type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];

export type FirstTouchAttribution = Record<AttributionParam, string | null> & {
  referrer: string;
  landing_path: string;
  ts: string;
};

function isFirstTouchAttribution(value: unknown): value is FirstTouchAttribution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }

  const attribution = value as Record<string, unknown>;
  return (
    ATTRIBUTION_PARAMS.every(
      (param) => typeof attribution[param] === "string" || attribution[param] === null,
    ) &&
    typeof attribution.referrer === "string" &&
    typeof attribution.landing_path === "string" &&
    typeof attribution.ts === "string"
  );
}

export function getFirstTouchAttribution(): FirstTouchAttribution | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (stored === null) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);
    if (isFirstTouchAttribution(parsed)) {
      return parsed;
    }
  } catch {
    // Invalid stored attribution is cleared below so a fresh first touch can be captured.
  }

  try {
    window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
  } catch {
    // Storage may be unavailable; captureFirstTouchAttribution will safely return null.
  }

  return null;
}

export function captureFirstTouchAttribution(): FirstTouchAttribution | null {
  if (typeof window === "undefined") {
    return null;
  }

  const existing = getFirstTouchAttribution();
  if (existing) {
    return existing;
  }

  try {
    if (window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY) !== null) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const attribution = {
      ...Object.fromEntries(ATTRIBUTION_PARAMS.map((param) => [param, params.get(param)])),
      referrer: document.referrer,
      landing_path: window.location.pathname,
      ts: new Date().toISOString(),
    } as FirstTouchAttribution;

    window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return null;
  }
}

export function getFirstTouchEventProperties(attribution: FirstTouchAttribution | null) {
  if (!attribution) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(attribution).map(([key, value]) => [`first_touch_${key}`, value]),
  );
}
